import { useEffect, useState } from "react";
import { Clock, Loader2, Video } from "lucide-react";
import { useFormContext, useWatch } from "react-hook-form";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import MyDialogContent from "@/components/MyDialogContent";
import RHFWrapper from "@/components/RHFWrapper";
import RhfInput from "@/components/rhfFields/RhfInput";
import RhfRadio from "@/components/rhfFields/RhfRadio";
import RhfSelect from "@/components/rhfFields/RhfSelect";
import { useGetData } from "@/hooks/useGetData";
import moment from "moment";
import AxiosInstance from "@/utils/AxiosInstance";
import { COMMON_TIMEZONES, FACILITY_TIMEZONES } from "@/utils/CommonData";
import { getErrToast, getPromise, getPromiseToast } from "@/utils/helpers";
import { getPatientTimeZone } from "../nursingNotes/nursingNoteHelpers";
import type { PatientV2 } from "../types";
import {
  TELEHEALTH_TIME_SLOT_OPTIONS,
  localDateYmd,
  scheduleTelehealthInitialValues,
  scheduleTelehealthSchema,
} from "./constants";

type ProviderRecord = {
  _id?: string;
  firstName?: string;
  lastName?: string;
  specializations?: string[] | string;
  timezone?: string;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patient: PatientV2 | null;
  patientId: string;
  tcmPlanId: string;
  rescheduleAppointmentId?: string;
  rescheduleReason?: string;
  onScheduled: (result: { meetingLink: string; notificationSent: boolean }) => void;
};

function text(value: unknown) {
  return String(value ?? "").trim();
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function meetingLinkFrom(payload: unknown): string {
  const root = asRecord(payload);
  const data = asRecord(root?.data);
  const candidates = [
    payload,
    root?.data,
    root?.appointment,
    data?.appointment,
    data?.telehealth,
  ];
  for (const candidate of candidates) {
    const row = asRecord(candidate);
    if (!row) continue;
    const direct = text(
      row.meetingLink ?? row.wherebyUrl ?? row.joinUrl ?? row.roomUrl ?? row.videoLink,
    );
    if (direct) return direct;
    const room = asRecord(row.room);
    const nested = text(room?.url ?? room?.roomUrl ?? room?.meetingLink ?? room?.hostRoomUrl);
    if (nested) return nested;
  }
  return "";
}

function recordId(value: unknown): string {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object" && "_id" in value) {
    return String((value as { _id?: string })._id ?? "");
  }
  return "";
}

function providerLabel(provider: ProviderRecord) {
  const name = [provider.firstName, provider.lastName].filter(Boolean).join(" ");
  const specialty = Array.isArray(provider.specializations)
    ? provider.specializations.filter(Boolean).join(", ")
    : String(provider.specializations ?? "").trim();
  if (name && specialty) return `${name} - ${specialty}`;
  return name || "Provider";
}

function entityId(value: unknown) {
  if (typeof value === "string") return value.trim();
  const row = asRecord(value);
  return text(row?._id ?? row?.id);
}

function unwrapAppointment(payload: unknown) {
  const root = asRecord(payload);
  const data = asRecord(root?.data);
  return asRecord(data?.appointment) ?? asRecord(data?.telehealth) ?? asRecord(root?.appointment) ?? data ?? root;
}

function toDateInput(value: unknown) {
  const raw = text(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const us = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (us) return `${us[3]}-${us[1].padStart(2, "0")}-${us[2].padStart(2, "0")}`;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return "";
  return localDateYmd(parsed);
}

function clockLabel(value: unknown) {
  const raw = text(value);
  const match = raw.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (match) return `${match[1].padStart(2, "0")}:${match[2]} ${match[3].toUpperCase()}`;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return "";
  let hours = parsed.getHours();
  const minutes = String(parsed.getMinutes()).padStart(2, "0");
  const meridiem = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${String(hours).padStart(2, "0")}:${minutes} ${meridiem}`;
}

function matchTimeSlot(row: Record<string, unknown>) {
  const raw = text(row.timeSlot).replace(/\s*\([^)]*\)\s*$/, "");
  const [startRaw, endRaw] = raw.split("-");
  const start = clockLabel(startRaw) || clockLabel(row.scheduledStartAt ?? row.startAt);
  const end = clockLabel(endRaw) || clockLabel(row.scheduledEndAt ?? row.endAt);
  const range = start && end ? `${start} - ${end}` : "";
  const found = TELEHEALTH_TIME_SLOT_OPTIONS.find((option) => {
    if (range && option.value.toLowerCase() === range.toLowerCase()) return true;
    return !!start && option.value.toLowerCase().startsWith(start.toLowerCase());
  });
  return found?.value || range || scheduleTelehealthInitialValues.timeSlot;
}

function yesNo(value: unknown) {
  if (value === true || text(value).toLowerCase() === "yes") return "yes";
  if (value === false || text(value).toLowerCase() === "no") return "no";
  return scheduleTelehealthInitialValues.sendNotification;
}

function patientFromAppointment(row: Record<string, unknown>, fallback: PatientV2 | null): PatientV2 | null {
  const patient = asRecord(row.patient) ?? asRecord(row.patientId) ?? asRecord(fallback);
  const organizationId =
    row.organizationId ?? row.organization ?? patient?.organizationId ?? fallback?.organizationId;
  if (!patient && !organizationId) return fallback;
  const code = text(patient?.patientId ?? fallback?.patientId);
  return {
    ...(fallback ?? {}),
    ...(patient ?? {}),
    _id: entityId(patient?._id ?? row.patientId) || fallback?._id,
    patientId: code && !/^[a-f0-9]{24}$/i.test(code) ? code : text(fallback?.patientId),
    organizationId: organizationId as PatientV2["organizationId"],
  };
}

function formValuesFromAppointment(row: Record<string, unknown>) {
  return {
    ...scheduleTelehealthInitialValues,
    assignedProvider: entityId(row.hostProvider ?? row.hostProviderId ?? row.provider ?? row.providerId),
    sendNotification: yesNo(row.sendNotification),
    appointmentDate: toDateInput(row.appointmentDate ?? row.date ?? row.scheduledStartAt),
    timeSlot: matchTimeSlot(row),
    clinicalNotes: text(row.visitNotes ?? row.clinicalNotes ?? row.notes),
    informedParties: row.informedConsent === true || row.informedParties === true,
    bookedOnProviderCalendar: row.ehrSlotConsent === true || row.bookedOnProviderCalendar === true,
  };
}

/** Hours behind Eastern. These four zones switch daylight saving on the same calendar day. */
const ZONE_HOURS_FROM_EASTERN: Record<string, number> = {
  "America/New_York": 0,
  "America/Chicago": -1,
  "America/Denver": -2,
  "America/Los_Angeles": -3,
};

function timezoneCode(iana: string) {
  const zone = iana.trim().toLowerCase();
  if (!zone) return "";
  const match = [...COMMON_TIMEZONES, ...FACILITY_TIMEZONES].find(
    (entry) => entry.value.toLowerCase() === zone,
  );
  return match?.label.match(/\(([^)]+)\)\s*$/)?.[1]?.trim() ?? "";
}

function formatClock(clock: string) {
  const parsed = moment(clock.trim(), ["h:mm A", "hh:mm A"], true);
  return parsed.isValid() ? parsed.format("hh:mm A") : clock.trim();
}

function slotRangeLabel(range: string, code: string) {
  const [start, end] = range.split(/\s*-\s*/);
  if (!start || !end) return range;
  const label = `${formatClock(start)} – ${formatClock(end)}`;
  return code ? `${label} ${code}` : label;
}

function providerSlotLabel(range: string, fromZone: string, toZone: string) {
  const from = ZONE_HOURS_FROM_EASTERN[fromZone];
  const to = ZONE_HOURS_FROM_EASTERN[toZone];
  if (from == null || to == null) return "";
  const [start, end] = range.split(/\s*-\s*/);
  if (!start || !end) return "";
  const convert = (clock: string) => {
    const parsed = moment(clock.trim(), ["h:mm A", "hh:mm A"], true);
    return parsed.isValid() ? parsed.add(to - from, "hours").format("hh:mm A") : "";
  };
  const nextStart = convert(start);
  const nextEnd = convert(end);
  if (!nextStart || !nextEnd) return "";
  return `${nextStart} – ${nextEnd}`;
}

function patientLine(patient: PatientV2 | null) {
  const name =
    [patient?.firstName, patient?.middleName, patient?.lastName].filter(Boolean).join(" ") ||
    "Patient";
  const code = String(patient?.patientId ?? "").trim();
  return code ? `${name} (${code})` : name;
}

function ConsentField({
  name,
  title,
  description,
}: {
  name: "informedParties" | "bookedOnProviderCalendar";
  title: string;
  description: string;
}) {
  const { control } = useFormContext();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="rounded-lg border border-[#E5E7EB] p-3">
          <div className="flex items-start gap-3">
            <FormControl>
              <Checkbox
                checked={field.value === true}
                onCheckedChange={(checked) => field.onChange(checked === true)}
                className="mt-0.5"
              />
            </FormControl>
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm font-medium text-[#111827]">
                  {title} <span>*</span>
                </p>
                {/* <span className="shrink-0 text-xs font-medium text-destructive">Required</span> */}
              </div>
              <p className="mt-1 text-xs leading-snug text-muted-foreground">{description}</p>
            </div>
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function AssignedProviderField({
  providers,
  loading,
  organizationId,
  preferredProviderId,
}: {
  providers: ProviderRecord[];
  loading: boolean;
  organizationId: string;
  preferredProviderId: string;
}) {
  const { setValue, getValues } = useFormContext();
  const options = providers
    .filter((provider) => provider._id)
    .map((provider) => ({
      label: providerLabel(provider),
      value: String(provider._id),
    }));

  useEffect(() => {
    if (!preferredProviderId || getValues("assignedProvider")) return;
    if (providers.some((provider) => String(provider._id) === preferredProviderId)) {
      setValue("assignedProvider", preferredProviderId);
    }
  }, [getValues, preferredProviderId, providers, setValue]);

  const placeholder = !organizationId
    ? "Patient has no organization"
    : loading
      ? "Loading providers..."
      : options.length === 0
        ? "No providers available"
        : "Select provider";

  return (
    <RhfSelect
      name="assignedProvider"
      label="Assigned Provider"
      required
      options={options}
      placeholder={placeholder}
      disabled={!organizationId || loading || options.length === 0}
    />
  );
}

function TimeSlotField({
  patientZone,
  providers,
}: {
  patientZone: string;
  providers: ProviderRecord[];
}) {
  const { control } = useFormContext();
  const timeSlot = text(useWatch({ control, name: "timeSlot" }));
  const assignedProvider = text(useWatch({ control, name: "assignedProvider" }));
  const patientCode = timezoneCode(patientZone);
  const provider = providers.find((item) => text(item._id) === assignedProvider);
  const providerZone = text(provider?.timezone);
  const providerCode = timezoneCode(providerZone);
  const providerRange =
    patientZone && providerZone ? providerSlotLabel(timeSlot, patientZone, providerZone) : "";
  const options = TELEHEALTH_TIME_SLOT_OPTIONS.map((option) => ({
    value: option.value,
    label: slotRangeLabel(option.value, patientCode),
  }));

  return (
    <div className="grid gap-2 self-start [&_.rhf-select__control]:h-9 [&_.rhf-select__control]:min-h-9 [&_.rhf-select__indicator]:p-1 [&_.rhf-select__value-container]:py-0">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm leading-none font-medium">Time Slot*</p>
        {patientCode ? (
          <p className="text-xs leading-none font-medium text-primary">Patient Time ({patientCode})</p>
        ) : null}
      </div>
      <RhfSelect
        name="timeSlot"
        label=""
        required
        options={options}
        placeholder="Select time slot"
      />
      {providerRange && providerCode ? (
        <p className="flex min-w-0 items-center gap-1.5 text-xs leading-none text-muted-foreground">
          <Clock className="size-3.5 shrink-0" />
          <span className="truncate">
            {providerRange} {providerCode} (Provider&apos;s Time)
          </span>
        </p>
      ) : null}
    </div>
  );
}

export function ScheduleTelehealthDialog({
  open,
  onOpenChange,
  patient,
  patientId,
  tcmPlanId,
  rescheduleAppointmentId,
  rescheduleReason = "",
  onScheduled,
}: Props) {
  const [loadedPatient, setLoadedPatient] = useState<PatientV2 | null>(null);
  const [loadedPatientId, setLoadedPatientId] = useState("");
  const [loadedPlanId, setLoadedPlanId] = useState("");
  const [loadedValues, setLoadedValues] = useState<Record<string, unknown> | null>(null);
  const [loadingAppointment, setLoadingAppointment] = useState(false);
  const [loadError, setLoadError] = useState("");
  const activePatient = loadedPatient ?? patient;
  const organizationId = recordId(activePatient?.organizationId);
  const preferredProviderId =
    text(loadedValues?.assignedProvider) || recordId(activePatient?.assignedProviderId);
  const { apiData, loading } = useGetData(
    "/providers",
    { orgId: organizationId },
    false,
    open && !!organizationId,
  );
  const providers = (apiData?.data?.providers ?? []) as ProviderRecord[];
  
  const patientZone = text(activePatient?.timezone) || getPatientTimeZone(activePatient) || "";
  console.log("patientZone", activePatient);
  const waitingForAppointment = !!rescheduleAppointmentId && !loadedValues;

  useEffect(() => {
    if (!open || !rescheduleAppointmentId) {
      setLoadedPatient(null);
      setLoadedPatientId("");
      setLoadedPlanId("");
      setLoadedValues(null);
      setLoadError("");
      setLoadingAppointment(false);
      return;
    }
    let cancelled = false;
    setLoadingAppointment(true);
    setLoadedValues(null);
    setLoadError("");
    AxiosInstance()
      .get(`/telehealth/appointments/${rescheduleAppointmentId}`)
      .then(({ data }) => {
        if (cancelled) return;
        const row = unwrapAppointment(data);
        if (!row) {
          setLoadError("Unable to load this appointment.");
          return;
        }
        const nextPatient = patientFromAppointment(row, patient);
        setLoadedPatient(nextPatient);
        setLoadedPatientId(entityId(row.patientId ?? row.patient) || patientId);
        setLoadedPlanId(entityId(row.tcmPlanId ?? row.tcmPlan) || tcmPlanId);
        setLoadedValues(formValuesFromAppointment(row));
      })
      .catch((error) => {
        if (cancelled) return;
        getErrToast(error);
        setLoadError("Unable to load this appointment.");
      })
      .finally(() => {
        if (!cancelled) setLoadingAppointment(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, patient, patientId, rescheduleAppointmentId, tcmPlanId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <MyDialogContent
        heading=""
        headerComponent={
          <p className="text-sm text-muted-foreground">Patient: {patientLine(activePatient)}</p>
        }
      >
        {waitingForAppointment ? (
          <div className="flex justify-center py-10">
            {loadError ? (
              <p className="text-sm text-muted-foreground">{loadError}</p>
            ) : (
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            )}
          </div>
        ) : (
        <RHFWrapper
          key={rescheduleAppointmentId ? `${rescheduleAppointmentId}-loaded` : "schedule"}
          className="space-y-4"
          initialValues={loadedValues ?? scheduleTelehealthInitialValues}
          isValidate
          validateSchema={scheduleTelehealthSchema}
          onSubmit={async (values) => {
            const { pr, resolveFn, rejectFn } = getPromise();
            getPromiseToast(pr);
            const resolvedPatientId = loadedPatientId || patientId;
            const resolvedPlanId = loadedPlanId || tcmPlanId;
            if (!resolvedPatientId || !resolvedPlanId) {
              rejectFn("Patient and TCM plan are required");
              return;
            }
            const notificationSent = values.sendNotification === "yes";
            const body = {
              patientId: resolvedPatientId,
              tcmPlanId: resolvedPlanId,
              hostProviderId: values.assignedProvider,
              appointmentDate: values.appointmentDate,
              timeSlot: values.timeSlot,
              sendNotification: notificationSent,
              visitNotes: values.clinicalNotes,
              informedConsent: values.informedParties === true,
              ehrSlotConsent: values.bookedOnProviderCalendar === true,
              ...(rescheduleAppointmentId ? { reason: rescheduleReason } : {}),
            };
            try {
              const response = await AxiosInstance().post(
                rescheduleAppointmentId
                  ? `/telehealth/appointments/${rescheduleAppointmentId}/reschedule`
                  : "/telehealth/appointments",
                body,
              );
              resolveFn(
                rescheduleAppointmentId
                  ? "Telehealth appointment rescheduled"
                  : "Telehealth appointment scheduled",
              );
              onOpenChange(false);
              onScheduled({
                meetingLink: meetingLinkFrom(response.data),
                notificationSent,
              });
            } catch (error) {
              rejectFn(error);
            }
          }}
        >
          <div className="grid items-start gap-4 sm:grid-cols-2">
            <AssignedProviderField
              providers={providers}
              loading={loading}
              organizationId={organizationId}
              preferredProviderId={preferredProviderId}
            />
            <RhfRadio
              name="sendNotification"
              label="Send Notification"
              required
              options={[
                { label: "Yes", value: "yes" },
                { label: "No", value: "no" },
              ]}
            />
            <RhfInput
              name="appointmentDate"
              label="Appointment Date"
              type="date"
              required
              min={localDateYmd()}
            />
            <TimeSlotField patientZone={patientZone} providers={providers} />
          </div>

          <RhfInput
            name="clinicalNotes"
            label="Clinical Visit Notes & Objectives"
            type="textarea"
            required
            rows={4}
          />

          <ConsentField
            name="informedParties"
            title="I consent I have informed both provider and patient about the appointment"
            description="Confirm that direct verbal information of the scheduled telehealth appointment has been delivered to both attending provider and patient/caregiver."
          />
          <ConsentField
            name="bookedOnProviderCalendar"
            title="I consent that I have booked the same slot on Provider's calendar through their valid EMR EHR"
            description="Verify that the appointment slot is synchronized and locked directly into the provider's active calendar inside the external EHR/EMR system."
          />

          <div className="flex items-center justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loadingAppointment}>
              <Video className="h-4 w-4" />
              Confirm & Schedule Telehealth
            </Button>
          </div>
        </RHFWrapper>
        )}
      </MyDialogContent>
    </Dialog>
  );
}
